// src/pages/Teams.js — fixed: delivery persons need password, hard delete added
import React, { useState, useEffect } from 'react';
import { get, post, patch, del } from '../utils/api';
import { Spinner, Alert, Modal, Input, Select } from '../components/Shared';

const ROLES = [
  { value:'stock_removal',  label:'Stock Removal' },
  { value:'checking',       label:'Checking' },
  { value:'packing',        label:'Packing' },
  { value:'delivery_person',label:'Delivery Person' },
  { value:'team_leader',    label:'Team Leader' },
];

const TEAM_IDS = {
  stock_removal:   '00000000-0000-0000-0000-000000000001',
  checking:        '00000000-0000-0000-0000-000000000002',
  packing:         '00000000-0000-0000-0000-000000000003',
  delivery_person: '00000000-0000-0000-0000-000000000004',
};

const ROLE_COLORS = {
  owner:           'bg-red-100 text-red-700',
  team_leader:     'bg-purple-100 text-purple-700',
  stock_removal:   'bg-yellow-100 text-yellow-700',
  checking:        'bg-blue-100 text-blue-700',
  packing:         'bg-indigo-100 text-indigo-700',
  delivery_person: 'bg-green-100 text-green-700',
};

export default function Teams() {
  const [users,   setUsers]   = useState([]);
  const [teams,   setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [form,    setForm]    = useState({ name:'', phone:'', role:'stock_removal', password:'' });
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');
  const [delUser,    setDelUser]    = useState(null);


  async function load() {
    try {
      const [uData, tData] = await Promise.all([
        get('/api/users?limit=200'),
        get('/api/teams'),
      ]);
      setUsers(uData.users || []);
      setTeams(tData.teams || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function addUser(e) {
    e.preventDefault();
    if (!form.password) {
      setMsg({ type:'error', text:'Password is required for all users including delivery persons' });
      return;
    }
    setSaving(true);
    try {
      await post('/api/users', {
        name:     form.name,
        phone:    form.phone,
        role:     form.role,
        password: form.password,
        team_id:  TEAM_IDS[form.role] || null,
      });
      setMsg({ type:'success', text:`${form.name} added successfully` });
      setShowAdd(false);
      setForm({ name:'', phone:'', role:'stock_removal', password:'' });
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
    } finally { setSaving(false); }
  }

  async function toggleActive(user) {
    try {
      await patch(`/api/users/${user.id}`, { is_active: !user.is_active });
      setMsg({ type:'success', text:`${user.name} ${user.is_active ? 'marked absent' : 'marked present'}` });
      load();
    } catch (err) { setMsg({ type:'error', text: err.message }); }
  }

  async function deleteUser(user) {
    try {
      await del(`/api/users/${user.id}`);
      setMsg({ type:'success', text:`${user.name} deleted` });
      setDelUser(null);
      load();
    } catch (err) {
      setMsg({ type:'error', text: err.message });
      setDelUser(null);
    }
  }

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.phone?.includes(search) ||
    u.role?.includes(search)
  );

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Team Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {users.filter(u => u.is_active).length} present today · All users login with phone + password
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-blue-900 text-white text-sm rounded-lg hover:bg-blue-800 font-semibold"
        >
          + Add Team Member
        </button>
      </div>

      {msg && <div className="mb-4"><Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} /></div>}

      {/* Team summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {teams.map(t => (
          <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t.name}</p>
            <p className="text-2xl font-bold text-gray-800">{t.active_members}</p>
            <p className="text-xs text-gray-400">of {t.max_members} max</p>
            <div className="w-full bg-gray-100 rounded-full h-1 mt-2">
              <div className="h-1 rounded-full bg-blue-500"
                style={{ width:`${Math.min(100,(t.active_members/t.max_members)*100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Info box about delivery person login */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4">
        <p className="text-blue-700 text-sm font-medium">
          ℹ️ All team members including delivery persons now use <strong>phone + password</strong> to login.
          Delivery persons use the delivery app at <strong>http://localhost:3003</strong>
        </p>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder="Search by name, phone or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Name','Phone','Role','Team','Last Login','Status','Actions'].map((h,i) => (
                <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                <td className="px-4 py-3 text-gray-500">{u.phone}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                    {u.role?.replace(/_/g,' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.team_name || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-IN') : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {u.is_active ? 'Present' : 'Absent'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.role !== 'owner' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleActive(u)}
                        className={`text-xs px-2 py-1 rounded font-medium ${u.is_active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                      >
                        {u.is_active ? 'Mark Absent' : 'Mark Present'}
                      </button>
  <button
                        onClick={() => setDelUser(u)}
                        className="text-xs px-2 py-1 rounded font-medium bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add user modal */}
      {showAdd && (
        <Modal title="Add Team Member" onClose={() => setShowAdd(false)}>
          <form onSubmit={addUser}>
            <Input
              label="Full Name"
              value={form.name}
              onChange={e => setForm({...form, name:e.target.value})}
              required
              placeholder="e.g. Ravi Kumar"
            />
            <Input
              label="Phone Number"
              value={form.phone}
              onChange={e => setForm({...form, phone:e.target.value})}
              required
              placeholder="+919876543210"
            />
            <Select
              label="Role"
              value={form.role}
              onChange={e => setForm({...form, role:e.target.value})}
              options={ROLES}
            />
            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={e => setForm({...form, password:e.target.value})}
              required
              placeholder="Set a permanent password (cannot be changed later)"
            />
            {form.role === 'delivery_person' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                🚚 Delivery persons login with this phone number and password at <strong>http://localhost:3003</strong>. This password cannot be changed after creation.
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2 bg-blue-900 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 disabled:opacity-60">
                {saving ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirmation modal */}}
      {delUser && (
        <Modal title="Delete User" onClose={() => setDelUser(null)}>
          <div className="text-center py-4">
            <p className="text-5xl mb-4">⚠️</p>
            <p className="text-gray-800 font-semibold text-lg mb-2">Delete {delUser.name}?</p>
            <p className="text-gray-500 text-sm mb-6">
              This permanently deletes the user account. Their historical records (invoices handled, deliveries made) will be preserved but their account will be removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDelUser(null)}
                className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteUser(delUser)}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-500"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
